// ./src/hooks/useGuestGroupSets.ts
//
// Loads an event's guest-group sets (active + disabled), by display order.
// `reload` refetches after a change.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { GuestGroupSetService, GuestGroupSet } from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseGuestGroupSetsReturn {
  sets: GuestGroupSet[];
  loading: boolean;
  /** True once the first fetch has completed (list may still be empty). */
  isLoaded: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
}

export const useGuestGroupSets = (eventId?: string | null): UseGuestGroupSetsReturn => {
  const [sets, setSets] = useState<GuestGroupSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!eventId) {
      setSets([]);
      setIsLoaded(true);
      return;
    }
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const result = await GuestGroupSetService.list(eventId, [STATUSES.ACTIVE, STATUSES.DISABLED]);

    if (myCall !== callIdRef.current) return;
    setLoading(false);
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setSets(result.data as GuestGroupSet[]);
    } else {
      setError(result);
      setSets([]);
    }
  }, [eventId]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    load();
  }, [load]);

  return { sets, loading, isLoaded, error, reload: load };
};
